// src/shared/changelog/useSeenEntries.ts
import { useCallback, useEffect, useState } from "react";
import { localStorageSeenStore, SEEN_EVENT } from "./seenStore";

/**
 * Stable identity for "nothing seen", so the initial state and the degraded one
 * do not hand consumers a fresh `Set` to re-derive from on every render.
 */
const NOTHING_SEEN: Set<string> = new Set();

interface SeenEntriesState {
  entries: Set<string>;
  /**
   * Whether the store has answered yet.
   *
   * Load-bearing for the What's new modal and nothing else: it opens on the
   * first **unseen** entry, and a set that is merely "empty so far" is
   * indistinguishable from "nothing read" — it would start at entry 1 and, on a
   * `✕`, mark the wrong one. The card can ignore it, because its `hasUnseen`
   * also waits on a fetch that resolves later than this read.
   */
  isLoaded: boolean;
}

/**
 * The entries this user has read, kept in sync across tabs, plus the one way to
 * add to them.
 *
 * The React seam over `SeenStore` (CTX-500 §2.3/§2.4). Three surfaces use it and
 * none of them touches `localStorage`: the footer card derives its tint from the
 * set (through `useUnseenRelease`), the What's new modal reads it to decide
 * where to open and writes to it on dismissal, and `/changelog` marks the whole
 * feed it just rendered.
 */
export function useSeenEntries(): {
  seenEntries: Set<string>;
  isLoaded: boolean;
  /** Marks entries read. A no-op for keys already in the set. */
  markSeen: (entryKeys: string[]) => void;
} {
  const [{ entries, isLoaded }, setState] = useState<SeenEntriesState>({
    entries: NOTHING_SEEN,
    isLoaded: false,
  });

  useEffect(() => {
    let alive = true;
    const sync = () => {
      localStorageSeenStore
        .read()
        .then((keys) => {
          if (alive) setState({ entries: new Set(keys), isLoaded: true });
        })
        .catch(() => {
          // The localStorage implementation swallows its own failures, so this
          // is here for the server-backed one §2.4 leaves room for. Degrading to
          // "loaded, nothing seen" re-announces a release; leaving `isLoaded`
          // false would freeze the modal on an empty dialog instead.
          if (alive) setState({ entries: NOTHING_SEEN, isLoaded: true });
        });
    };

    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(SEEN_EVENT, sync);
    return () => {
      alive = false;
      window.removeEventListener("storage", sync);
      window.removeEventListener(SEEN_EVENT, sync);
    };
  }, []);

  /**
   * Fire-and-forget: the write broadcasts `SEEN_EVENT`, and the listener above
   * re-reads. So this hook's own state updates through exactly the path a write
   * from another tab takes, and there is only one way the set can change.
   */
  const markSeen = useCallback((entryKeys: string[]) => {
    void localStorageSeenStore.add(entryKeys);
  }, []);

  return { seenEntries: entries, isLoaded, markSeen };
}
